import type { OnboardingApplicationHydrated } from '../../../lib/flotiq-client/types/api-response';
import type { UserLocalStorage } from '../../../types/localStorage';

import { use, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import useLocalStorageState from 'use-local-storage-state';

import { postSpacePurge } from '../../../lib/flotiq-client';
import { parseResponseErrors } from '../../../lib/flotiq-client/response-errors';
import { getTestProps, getTranslatedField } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import Loader from '../../../components/Loader/Loader';

import { useContentObjects, useContentTypes } from '../../../hooks/api';
import useToken from '../../../hooks/useToken';

import { defaultTextRender, useModals } from '../../../contexts/ModalContext';
import UserContext from '../../../contexts/UserContext';

import PopulateSpaceModalContent from '../../../layout/PopulateSpaceModal/content/PopulateSpaceIntruductionContent';
import getApiKeys from '../../../layout/PopulateSpaceModal/populateSpaceSteps/getGlobalApiKeys';
import syncSpace from '../../../layout/PopulateSpaceModal/populateSpaceSteps/syncSpace';
import SpaceSettingsSection from '../SpaceSettingsSection/SpaceSettingsSection';
import ManageDataModalContent from './modal/ManageDataModalContent';

const testId = 'manage-data-settings';

interface ManageDataSettingsProps {
  spaceId?: string;
}

const ManageDataSettings = ({ spaceId }: ManageDataSettingsProps) => {
  const { isAdmin } = use(UserContext);
  const { t } = useTranslation();
  const jwt = useToken();
  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');
  const modal = useModals();

  const [isClearing, setIsClearing] = useState(false);
  const [isFilling, setIsFilling] = useState(false);

  const ctdParams = useMemo(() => ({ limit: 1000 }), []);
  const { data: ctds, isLoading: areCtdsLoading } = useContentTypes(ctdParams);

  const mediaParams = useMemo(() => ({ limit: 1 }), []);
  const { data: media, isLoading: isMediaLoading } = useContentObjects(
    '_media',
    mediaParams,
  );

  const hasAnyData = useMemo(
    () => (ctds ?? []).some((ctd) => !ctd.internal) || (media?.length ?? 0) > 0,
    [ctds, media],
  );

  const handleClearAll = useCallback(
    async (confirmedSpaceId: string) => {
      if (!spaceId || !jwt) return;

      setIsClearing(true);
      try {
        const { body, status, ok } = await postSpacePurge(jwt, spaceId, {
          id: spaceId,
          spaceId: confirmedSpaceId,
        });

        if (!ok) throw parseResponseErrors(body, status);

        window.location.reload();
      } catch {
        toast.error(t('Form.CommunicationErrorMessage'));
      } finally {
        setIsClearing(false);
      }
    },
    [jwt, spaceId, t],
  );

  const openClearAllModal = useCallback(async () => {
    const result = await modal({
      title: defaultTextRender.warning(t('Global.Warning')),
      content: (
        <ManageDataModalContent expectedSpaceId={spaceId} testId={testId} />
      ),
      size: 'md',
      dialogAdditionalClasses: 'max-w-[550px]',
      hideClose: false,
    });

    if (result) await handleClearAll(result);
  }, [modal, t, spaceId, handleClearAll]);

  const handleFillDemoData = useCallback(
    async (application: OnboardingApplicationHydrated) => {
      if (!jwt) return;

      modal.close('fill-demo-data-modal');

      setIsFilling(true);
      try {
        const apiKeys = await getApiKeys(jwt, t);
        const sourceApiKey = getTranslatedField(
          application,
          'space_ro_api_key',
          i18n.language,
        );

        await syncSpace(apiKeys.rwApiKey, sourceApiKey);
        window.location.reload();
      } catch (error) {
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : t('SpaceSettings.ManageData.FillDemoDataError'),
        );
      } finally {
        setIsFilling(false);
      }
    },
    [jwt, modal, t],
  );

  const openFillDemoDataModal = useCallback(async () => {
    await modal({
      id: 'fill-demo-data-modal',
      content: (
        <PopulateSpaceModalContent
          userName={user?.data.firstName ?? ''}
          onApplicationSelect={handleFillDemoData}
          testId={testId}
        />
      ),
      size: 'inset',
      dialogAdditionalClasses: 'bg-slate-50',
      contentAdditionalClasses: 'h-full p-4 lg:p-12',
      hideClose: true,
    });
  }, [modal, user?.data.firstName, handleFillDemoData]);

  const isLoading =
    isClearing || isFilling || areCtdsLoading || isMediaLoading || !spaceId;

  return (
    <SpaceSettingsSection
      title={t('SpaceSettings.ManageData.Title')}
      description={t('SpaceSettings.ManageData.Description')}
    >
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {!areCtdsLoading && !isMediaLoading && !hasAnyData && (
          <Button
            disabled={isLoading}
            iconImage={
              isFilling ? <Loader type="spinner-grid" size="tiny" /> : null
            }
            onClick={openFillDemoDataModal}
            {...getTestProps(testId, 'fill-demo-data', 'testId')}
          >
            {t('SpaceSettings.ManageData.FillDemoData')}
          </Button>
        )}

        {isAdmin && (
          <Button
            buttonColor={'red'}
            disabled={isLoading || !hasAnyData}
            iconImage={
              isClearing ? <Loader type="spinner-grid" size="tiny" /> : null
            }
            onClick={openClearAllModal}
            {...getTestProps(testId, 'clear-all', 'testId')}
          >
            {t('SpaceSettings.ManageData.ClearAll')}
          </Button>
        )}
      </div>
    </SpaceSettingsSection>
  );
};

export default ManageDataSettings;
